import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Pointer, Volume2, VolumeX, X } from 'lucide-react';
import { speakStep, stopSpeaking, voiceAvailable } from './voice';
import { GuideAvatar } from './Avatar';
import { useApp } from '../../context/AppContext';
import { useT } from '../../i18n';
import { TOURS, type TourDef } from './steps';

interface TourState { id: TourDef['id']; step: number }
interface TourCtx { start: (id: TourDef['id']) => void; active: boolean }

const Ctx = createContext<TourCtx>({ start: () => undefined, active: false });
export const useTour = () => useContext(Ctx);

const KEY = 'sc.tour';
const load = (): TourState | null => {
  try {
    const v = sessionStorage.getItem(KEY);
    return v ? (JSON.parse(v) as TourState) : null;
  } catch {
    return null;
  }
};
const save = (s: TourState | null) => {
  try {
    if (s) sessionStorage.setItem(KEY, JSON.stringify(s));
    else sessionStorage.removeItem(KEY);
  } catch {
    /* private mode: tour still works for this page view */
  }
};

// Demo-only guided tour: opens each screen in turn and points at one part of it.
export function TourProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TourState | null>(load);
  // The guiding hand: where it points (screen position) and a tap animation key.
  const [hand, setHand] = useState<{ x: number; y: number; tap: number } | null>(null);
  const { setRole, setFacilityId, login, lang } = useApp();
  const [voiceOn, setVoiceOn] = useState(() => {
    try {
      return sessionStorage.getItem('sc.tourVoice') !== 'off';
    } catch {
      return true;
    }
  });
  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    try {
      sessionStorage.setItem('sc.tourVoice', next ? 'on' : 'off');
    } catch {
      /* ignore */
    }
    if (!next) stopSpeaking();
    else if (state) sayStep(state, true);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();

  // Speak a step right away. Must run inside the tap itself, or iPhones block the sound.
  const sayStep = (s: TourState, on = voiceOn) => {
    const st = TOURS[s.id].steps[s.step];
    if (on && st) speakStep(`${s.id}-${st.key}`, `${t(`tour.${s.id}.${st.key}.title`)}. ${t(`tour.${s.id}.${st.key}.body`)}`, lang);
  };

  const set = (s: TourState | null) => {
    setState(s);
    save(s);
    if (!s) stopSpeaking();
  };
  const start = (id: TourDef['id']) => {
    const def = TOURS[id];
    setRole(def.role);
    setFacilityId(def.facilityId);
    login();
    const s = { id, step: 0 };
    set(s);
    sayStep(s);
  };

  const def = state ? TOURS[state.id] : null;
  const step = def && state ? def.steps[state.step] : null;

  // Open the right screen, then highlight the target once it has loaded.
  useEffect(() => {
    if (!step) return;
    if (location.pathname !== step.route) {
      navigate(step.route);
      return;
    }
    if (!step.target) {
      setHand(null);
      return;
    }
    let target: Element | null = null;
    let tries = 0;
    // Keep the hand on the item as the page scrolls.
    const place = (tap = false) => {
      if (!target) return;
      const r = target.getBoundingClientRect();
      const x = r.left + Math.min(r.width / 2, 72);
      const y = r.top + Math.min(r.height / 2, 36);
      setHand((h) => ({ x, y, tap: tap ? (h?.tap ?? 0) + 1 : h?.tap ?? 0 }));
    };
    const onScroll = () => place();
    let settle = 0;
    const timer = window.setInterval(() => {
      target = document.querySelector(`[data-testid="${step.target}"]`);
      if (target || ++tries > 40) {
        window.clearInterval(timer);
        if (target) {
          target.classList.add('tour-target');
          document.body.classList.add('tour-dim');
          // Put it just below the top bar, so the tour card (bottom of the screen) never covers it.
          const top = target.getBoundingClientRect().top + window.scrollY - 150;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          window.addEventListener('scroll', onScroll, { passive: true });
          // After scrolling settles, glide the hand there and "tap".
          settle = window.setTimeout(() => place(true), 650);
        }
      }
    }, 100);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(settle);
      window.removeEventListener('scroll', onScroll);
      document.querySelectorAll('.tour-target').forEach((x) => x.classList.remove('tour-target'));
      document.body.classList.remove('tour-dim');
    };
  }, [step, location.pathname, navigate]);

  const title = state && step ? t(`tour.${state.id}.${step.key}.title`) : '';
  const body = state && step ? t(`tour.${state.id}.${step.key}.body`) : '';

  const n = def?.steps.length ?? 0;
  const i = state?.step ?? 0;
  const go = (d: number) => {
    if (!state) return;
    const s = { ...state, step: Math.min(Math.max(i + d, 0), n - 1) };
    set(s);
    sayStep(s);
  };

  return (
    <Ctx.Provider value={{ start, active: !!state }}>
      {children}
      {state && step?.target && hand && (
        <div className="tour-hand print:hidden" style={{ left: hand.x, top: hand.y }} aria-hidden="true" data-testid="tour-hand">
          <span key={hand.tap} className="tour-tap" />
          <Pointer className="h-9 w-9 text-white drop-shadow-lg" strokeWidth={1.8} fill="var(--sc-deep)" />
        </div>
      )}
      {state && step && (
        <div className="fixed inset-x-0 bottom-20 z-50 px-3 print:hidden lg:bottom-6 lg:left-auto lg:right-6 lg:w-96 lg:px-0" role="dialog" aria-live="polite" data-testid="tour-card">
          <div className="rounded-2xl bg-sc-deep p-4 text-white shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide opacity-80" data-testid="tour-progress">{t('tour.progress', { n: i + 1, total: n })}</p>
              <div className="-m-2 flex">
                {voiceAvailable() && (
                  <button type="button" onClick={toggleVoice} aria-label={voiceOn ? t('tour.voiceOff') : t('tour.voiceOn')} aria-pressed={voiceOn} data-testid="tour-voice" className="rounded-full p-2 opacity-90 hover:bg-white/10">
                    {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                  </button>
                )}
                <button type="button" onClick={() => set(null)} aria-label={t('tour.exit')} data-testid="tour-exit" className="rounded-full p-2 opacity-80 hover:bg-white/10"><X className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-1 flex items-start gap-3">
              <GuideAvatar size={56} />
              <div className="min-w-0">
                <h2 className="text-lg font-bold">{title}</h2>
                <p className="mt-1 text-sm leading-relaxed opacity-95">{body}</p>
              </div>
            </div>
            {i === 0 && voiceOn && <p className="mt-2 text-xs opacity-80" data-testid="tour-sound-hint">{t('tour.soundHint')}</p>}
            <div className="mt-3 flex gap-2">
              {i > 0 && <button type="button" onClick={() => go(-1)} data-testid="tour-back" className="flex h-11 items-center gap-1 rounded-xl border border-white/40 px-3 text-sm font-semibold"><ArrowLeft className="h-4 w-4" />{t('tour.back')}</button>}
              {i < n - 1 ? (
                <button type="button" onClick={() => go(1)} data-testid="tour-next" className="flex h-11 flex-1 items-center justify-center gap-1 rounded-xl bg-white px-3 text-sm font-bold text-sc-deep">{t('tour.next')}<ArrowRight className="h-4 w-4" /></button>
              ) : (
                <button type="button" onClick={() => set(null)} data-testid="tour-finish" className="flex h-11 flex-1 items-center justify-center rounded-xl bg-white px-3 text-sm font-bold text-sc-deep">{t('tour.finish')}</button>
              )}
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
